/**
 * Lazy-loaded component registry
 *
 * Centralized lazy component definitions for Virtual Hardhat.
 * Components are organized by category for easy navigation and maintenance.
 * All components use React.lazy() for code splitting via Vite.
 *
 * Note: Vite automatically generates chunk names based on file paths.
 * Magic comments are optional for custom naming.
 *
 * Note: Components are exported unconditionally. Feature flag checks are
 * handled at the route level in routeConfig.js to avoid duplication.
 */

import React from 'react';

// ============================================================================
// PRODUCTION DASHBOARD PAGES
// ============================================================================

/**
 * Command Center Full - The unified contractor dashboard
 * Main production dashboard with all features
 */
export const CommandCenterFull = React.lazy(
  () => import(/* vite-chunk-name: 'command-center' */ '../pages/dashboard/CommandCenterFull')
);

/**
 * Command Center route shell — dashboard page (CommandBar mounts in AppShell).
 * This is what /command-center renders in production.
 */
export const CommandCenterRoute = React.lazy(
  () => import(/* vite-chunk-name: 'command-center' */ '../pages/dashboard/CommandCenterRoute')
);

/**
 * Command Center - World-class dark industrial UI for enterprise
 * Apple + Tesla/Rivian inspired design with ring-based metrics
 * This is the main production dashboard component
 */
export const DarkCommandCenter = React.lazy(
  () => import(/* vite-chunk-name: 'command-center' */ '../pages/dashboard/CommandCenter')
);

// REMOVED: DarkDemoLayout - demo functionality moved to vh-demo-web

/**
 * Production Dashboard Pages - Core dashboard functionality
 */
export const DarkProjects = React.lazy(
  () => import(/* vite-chunk-name: 'dashboard' */ '../pages/dashboard/Projects')
);
export const DarkDirectory = React.lazy(
  () => import(/* vite-chunk-name: 'dashboard' */ '../pages/dashboard/Directory')
);
export const DarkReports = React.lazy(
  () => import(/* vite-chunk-name: 'dashboard' */ '../pages/dashboard/Reports')
);
export const DarkTraining = React.lazy(
  () => import(/* vite-chunk-name: 'dashboard' */ '../pages/dashboard/Training')
);
export const DarkLearningAdmin = React.lazy(
  () => import(/* vite-chunk-name: 'dashboard' */ '../pages/dashboard/LearningAdmin')
);
export const DarkSchedule = React.lazy(
  () => import(/* vite-chunk-name: 'dashboard' */ '../pages/dashboard/Schedule')
);
export const DarkEquipment = React.lazy(
  () => import(/* vite-chunk-name: 'dashboard' */ '../pages/dashboard/Equipment')
);
export const DarkWorkerHours = React.lazy(
  () => import(/* vite-chunk-name: 'dashboard' */ '../pages/dashboard/WorkerHours')
);
export const DarkOSHACompliance = React.lazy(
  () => import(/* vite-chunk-name: 'dashboard' */ '../pages/dashboard/OSHACompliance')
);
export const DarkIncidents = React.lazy(
  () => import(/* vite-chunk-name: 'dashboard' */ '../pages/dashboard/Incidents')
);
export const DarkAutomationSettings = React.lazy(
  () => import(/* vite-chunk-name: 'dashboard' */ '../pages/dashboard/AutomationSettings')
);
export const DarkAIIntelligence = React.lazy(
  () => import(/* vite-chunk-name: 'dashboard' */ '../pages/dashboard/AIIntelligence')
);
export const DarkWorkers = React.lazy(
  () => import(/* vite-chunk-name: 'dashboard' */ '../pages/dashboard/Workers')
);
export const DarkAssetRegistry = React.lazy(
  () => import(/* vite-chunk-name: 'dashboard' */ '../pages/dashboard/AssetRegistry')
);
export const DarkAnalytics = React.lazy(
  () => import(/* vite-chunk-name: 'dashboard' */ '../pages/dashboard/Analytics')
);

/**
 * Foreman Dashboard - Role-based dashboard for foremen/supervisors
 */
export const DarkForemanDashboard = React.lazy(
  () => import(/* vite-chunk-name: 'dashboard' */ '../pages/dashboard/ForemanDashboard')
);

export const PlanningSessionPage = React.lazy(
  () => import(/* vite-chunk-name: 'dashboard' */ '../pages/dashboard/PlanningSessionPage')
);

/**
 * Admin Management Pages - User management and role configuration
 */
export const DarkAdminUsers = React.lazy(
  () => import(/* vite-chunk-name: 'dashboard' */ '../pages/dashboard/AdminUsers')
);
export const DarkActivityLog = React.lazy(
  () => import(/* vite-chunk-name: 'dashboard' */ '../pages/dashboard/ActivityLog')
);

/**
 * Common Pages
 */
export const Directory = React.lazy(
  () => import('../pages/Directory')
);

// ============================================================================
// DASHBOARDS
// ============================================================================

// REMOVED: UnifiedDashboard, CommandCenterDemo - deleted, use CommandCenterFull instead

/**
 * AI Intelligence Center - Agent monitoring and proposal management
 */
export const AIIntelligencePage = React.lazy(
  () => import('../pages/AIIntelligencePage')
);

// REMOVED: GlassDashboard - design showcase deleted

/**
 * Admin and Compliance Dashboards
 */
export const EmployeeComplianceReports = React.lazy(
  () => import('../pages/EmployeeComplianceReports')
);

// ============================================================================
// ASSESSMENT PAGES
// ============================================================================

export const SafetyAssessment = React.lazy(
  () => import('../pages/SafetyAssessment')
);
export const HazardReport = React.lazy(
  () => import('../pages/HazardReport')
);
export const SafetyQuiz = React.lazy(
  () => import('../pages/SafetyQuiz')
);

// ============================================================================
// SETTINGS & MANAGEMENT
// ============================================================================

export const Billing = React.lazy(
  () => import('../pages/Billing')
);
export const SettingsHub = React.lazy(
  () => import(/* vite-chunk-name: 'settings' */ '../pages/settings/SettingsHub')
);
export const IntegrationsOverviewPage = React.lazy(
  () => import(/* vite-chunk-name: 'settings' */ '../pages/settings/IntegrationsOverviewPage')
);
export const GCSettings = React.lazy(
  () => import('../pages/GCSettings')
);
export const AutomationSettings = React.lazy(
  () => import('../pages/settings/AutomationSettings')
);
export const NotificationPreferencesPage = React.lazy(
  () => import('../pages/settings/NotificationPreferencesPage')
);
export const ProcoreIntegrationPage = React.lazy(
  () => import(/* vite-chunk-name: 'settings' */ '../pages/settings/ProcoreIntegrationPage')
);

// ============================================================================
// SC/GC PAGES
// ============================================================================

/**
 * Production SC Pages
 */
export const Compliance = React.lazy(
  () => import('../pages/sc/Compliance')
);
export const Equipment = React.lazy(
  () => import('../pages/sc/Equipment')
);
// Worker Hours Management - Manual timesheet entry and TRIR calculations
export const WorkerHours = React.lazy(
  () => import('../pages/sc/WorkerHours')
);
// OSHA 300 Log - OSHA recordkeeping and safety metrics
export const OSHACompliance = React.lazy(
  () => import('../pages/sc/OSHACompliance')
);
// Incident Reporting - Report and track safety incidents
export const IncidentForm = React.lazy(
  () => import('../pages/IncidentForm')
);
// Incidents Dashboard - Dashboard-style incident list and metrics
export const Incidents = React.lazy(
  () => import('../pages/sc/Incidents')
);
export const WorkerOnboardingWizard = React.lazy(
  () => import('../pages/onboarding/WorkerOnboardingWizard')
);

/**
 * Worker Signup (via invite token)
 */
export const WorkerSignup = React.lazy(
  () => import('../pages/auth/WorkerSignup')
);

/**
 * Team Invite - Dashboard user signup (via invite token)
 */
export const TeamInvite = React.lazy(
  () => import('../pages/auth/TeamInvite')
);

/**
 * Reset Password - Password reset flow
 */
export const ResetPassword = React.lazy(
  () => import('../pages/ResetPassword')
);

/**
 * Production Reports Pages
 */
export const SCReports = React.lazy(
  () => import('../pages/sc/Reports')
);
export const GCReports = React.lazy(
  () => import('../pages/gc/Reports')
);

// ============================================================================
// COMMON PAGES
// ============================================================================

// REMOVED: Dashboard (common) - deleted, use CommandCenterFull instead
export const Projects = React.lazy(
  () => import('../pages/common/Projects')
);
export const DeepResearch = React.lazy(
  () => import('../pages/DeepResearch')
);
export const Help = React.lazy(
  () => import('../components/UnderConstruction')
);
export const Schedule = React.lazy(
  () => import('../pages/Schedule')
);

// REMOVED: MLDashboard - deleted

// REMOVED: DemoAuthWrapper - demo functionality moved to vh-demo-web

// REMOVED: TerminalDemo, UnifiedDashboardDemo - demo functionality moved to vh-demo-web
